import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import {
  makeStyles,
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Icon,
  Tooltip,
  IconButton,
  Chip,
} from '@material-ui/core';
import moment from 'moment';
import { GlobalContext } from '../../global-context';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import fragments from '../utils/graphql/fragments';
import BoothIdLink from '../reusable/BoothIdLink';
import Loading from '../utils/Loading';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    top: 64, // Start below the main website header (typical Material-UI AppBar height)
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    zIndex: 100, // Lower z-index to stay below main header but above page content
  },
  timelineContainer: {
    flex: 1,
    overflowX: 'auto',
    overflowY: 'auto',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    position: 'relative',
  },
  dateHeader: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 90, // Increased height for consistent layout
    maxHeight: 90, // Fixed height to prevent expansion
  },
  dateCell: {
    padding: theme.spacing(0.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start', // Changed to flex-start for consistent positioning
    backgroundColor: theme.palette.background.paper,
    fontSize: '0.75rem',
    overflow: 'hidden', // Prevent content overflow
    position: 'relative', // Enable absolute positioning for stats
    cursor: 'pointer', // Make it clear the date headers are clickable
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.today': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
    },
    '&.weekend': {
      backgroundColor: theme.palette.grey[200], // More prominent gray for weekends
      '&.today': {
        backgroundColor: theme.palette.primary.main, // Ensure today styling takes precedence
      },
    },
    '&.selected': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      fontWeight: 600,
      '&.today': {
        backgroundColor: theme.palette.primary.main, // Today takes precedence over selected
      },
    },
  },
  dateInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 4,
    minHeight: 42, // Fixed height for date info section
    justifyContent: 'center',
  },
  dateDay: {
    fontSize: '0.8rem',
    fontWeight: 500,
    lineHeight: 1,
  },
  dateNum: {
    fontSize: '1.1rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  dateMonth: {
    fontSize: '0.7rem',
    opacity: 0.7,
    lineHeight: 1,
  },
  dateStats: {
    display: 'flex',
    flexDirection: 'row',
    gap: 0, // Remove gap between boxes
    width: '100%',
    minHeight: 16, // Fixed height for single row
    alignItems: 'stretch', // Make boxes fill the height
    justifyContent: 'stretch',
    position: 'absolute', // Pin to bottom of date cell
    bottom: 0,
    left: 0,
    right: 0,
  },
  statBox: {
    flex: '1 1 0%', // Equal distribution of width
    minWidth: 0, // Allow shrinking
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6rem',
    fontWeight: 600,
    margin: 0,
    padding: 0,
    borderRadius: 0, // No border radius for seamless connection
    textAlign: 'center',
  },
  rentalRow: {
    display: 'flex',
    minHeight: 25,
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  rentalRowDense: {
    minHeight: 45, // Increased from 40px to 45px for more room
  },
  rentalRowExpanded: {
    minHeight: 80, // Much taller for detailed view
  },
  rentalCell: {
    height: '100%',
    borderRight: `1px solid ${theme.palette.divider}`,
    position: 'relative',
  },
  rentalBar: {
    position: 'absolute',
    height: 20,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 0.5),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    top: 2.5,
    zIndex: 2,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    },
  },
  rentalBarDense: {
    height: 35, // Increased from 30px to 35px
    top: 5, // Keep centered in the 45px row
  },
  rentalBarExpanded: {
    height: 70,
    top: 5,
    padding: theme.spacing(1),
  },
  rentalBarFirst: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  rentalBarLast: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  rentalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    color: 'white',
    fontSize: '0.65rem',
    lineHeight: 1.1,
    minWidth: 0,
    width: '100%',
  },
  rentalContentDense: {
    fontSize: '0.7rem',
    lineHeight: 1.2,
  },
  rentalContentExpanded: {
    fontSize: '0.8rem',
    lineHeight: 1.3,
    gap: theme.spacing(0.5),
  },
  rentalTitle: {
    fontWeight: 600,
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  rentalSubtitle: {
    fontSize: '0.6rem',
    opacity: 0.9,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  'phase-shipping': {
    background: '#FF9800',
  },
  'phase-delivered': {
    background: '#4CAF50',
  },
  'phase-event': {
    background: '#2E7D32',
  },
  'phase-packing': {
    background: '#9C27B0',
  },
  'phase-returning': {
    background: '#607D8B',
  },
  noRentals: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  todayIndicator: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: theme.palette.error.main,
    zIndex: 3,
    left: '50%',
    marginLeft: -1,
  },
  floatingControls: {
    position: 'fixed',
    bottom: 20,
    left: 20,
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 280, // Fixed width to prevent layout shifts
  },
  dateRangeDisplay: {
    flex: 1,
    textAlign: 'left',
    minWidth: 0, // Allow text to truncate if needed
  },
  todayButton: {
    flexShrink: 0, // Prevent button from shrinking
  },
  zoomControls: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  zoomButton: {
    minWidth: 32,
    height: 32,
  },
  zoomLabel: {
    minWidth: 70,
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  viewModeToggle: {
    position: 'fixed',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  viewModeButton: {
    minWidth: 60,
    height: 32,
    fontSize: '0.7rem',
  },
  // Weekend background stripe
  weekendStripe: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.02)', // Very subtle gray overlay
    zIndex: 0, // Behind everything else
    pointerEvents: 'none', // Don't interfere with clicking
  },
  // Floating booth header
  boothHeader: {
    position: 'sticky',
    left: 0,
    width: 80,
    backgroundColor: theme.palette.background.paper,
    borderRight: `2px solid ${theme.palette.divider}`,
    zIndex: 10, // Higher than rental bars to stay on top
    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
  },
  boothHeaderContainer: {
    position: 'sticky',
    backgroundColor: theme.palette.background.paper,
    left: 0,
    width: 80,
    height: 0, // No height so it doesn't take up space
    zIndex: 15, // Higher than date header
    pointerEvents: 'none', // Allow clicking through to timeline content
  },
  boothHeaderRow: {
    position: 'absolute',
    width: 80,
    height: 25,
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
    // backgroundColor: theme.palette.background.paper, // Removed for transparency
    // borderRight: `2px solid ${theme.palette.divider}`, // Removed border styling
    // borderBottom: `1px solid ${theme.palette.divider}`, // Removed border styling
    //boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
    padding: theme.spacing(0, 0.5),
    fontSize: '0.7rem',
    fontWeight: 500,
    pointerEvents: 'auto', // Re-enable clicking on the booth links
  },
  boothHeaderOverlay: {
    position: 'fixed',
    top: 64, // Below main header
    left: 0,
    width: 80,
    height: 'calc(100vh - 64px)',
    zIndex: 5, // Lower than date header (10) so it renders behind it
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  // In-row booth header that's pinned to the left
  inRowBoothHeader: {
    position: 'sticky',
    left: 0,
    top: 0,
    width: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    // backgroundColor: theme.palette.background.paper, // Removed for transparency
    padding: theme.spacing(0, 0.5),
    fontSize: '0.7rem',
    fontWeight: 500,
    zIndex: 5, // Above rental bars but below date header
    // boxShadow: '2px 0 4px rgba(0,0,0,0.1)', // Removed drop shadow
  },
  // Timeline row with subtle hover effect
  timelineRow: {
    position: 'relative',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      zIndex: 1, // Behind rental bars and content
    },
  },
}));

const GET_TIMELINE_ORDERS = gql`
  query getTimelineOrders($startDate: date!, $endDate: date!) {
    orders(
      where: {
        active: { _eq: true }
        _or: [
          { ship_date: { _gte: $startDate, _lte: $endDate } }
          { arrival_date: { _gte: $startDate, _lte: $endDate } }
          { event_date: { _gte: $startDate, _lte: $endDate } }
          { return_ship_date: { _gte: $startDate, _lte: $endDate } }
          { return_date: { _gte: $startDate, _lte: $endDate } }
          { _and: [{ ship_date: { _lte: $startDate } }, { return_date: { _gte: $endDate } }] }
        ]
      }
      order_by: { ship_date: asc }
    ) {
      ...Order
    }
  }
  ${fragments.order}
`;

const GET_BOOTH_COUNT = gql`
  query getBoothCount {
    booths_aggregate(where: { active: { _eq: true } }) {
      aggregate {
        count
      }
    }
  }
`;

export default function RentalTimeline() {
  const classes = useStyles();
  const ctx = useContext(GlobalContext);
  const scrollRef = useRef(null);
  const initialScrollDone = useRef(false);

  // State for zoom level (7-28 days) - default to 28 days
  const [daysVisible, setDaysVisible] = useState(28);

  // State for rendered date range (much larger than visible)
  const [renderedStartDate, setRenderedStartDate] = useState(moment().subtract(60, 'days'));
  const [renderedEndDate, setRenderedEndDate] = useState(moment().add(60, 'days'));

  // State for orders data
  const [orders, setOrders] = useState([]);

  // State for timeline container width
  const [containerWidth, setContainerWidth] = useState(1200);

  // State for current scroll position - needed for real-time date range updates
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Persistent booth row mapping to prevent visual jumping
  const [boothRowMapping, setBoothRowMapping] = useState({});
  const [nextRowIndex, setNextRowIndex] = useState(0);

  // State for sorting by selected date
  const [selectedSortDate, setSelectedSortDate] = useState(moment().format('YYYY-MM-DD'));

  // State for view mode (dense or expanded)
  const [viewMode, setViewMode] = useState('dense');

  // Calculate cell width based on container width and days visible
  const cellWidth = useMemo(() => {
    return Math.max(60, Math.floor(containerWidth / daysVisible)); // Minimum 60px per day
  }, [containerWidth, daysVisible]);

  // Calculate visible date range based on current scroll position
  const visibleRange = useMemo(() => {
    if (!initialScrollDone.current) {
      const today = moment();
      const halfDays = Math.floor(daysVisible / 2);
      return {
        start: today.clone().subtract(halfDays, 'days'),
        end: today.clone().add(halfDays, 'days'),
      };
    }

    const startIndex = Math.floor(scrollLeft / cellWidth);
    const endIndex = startIndex + daysVisible;

    const start = renderedStartDate.clone().add(startIndex, 'days');
    const end = renderedStartDate.clone().add(endIndex, 'days');

    return { start, end };
  }, [renderedStartDate, daysVisible, cellWidth, scrollLeft]);

  // Generate array of all rendered dates (large buffer)
  const renderedDates = useMemo(() => {
    const dates = [];
    const current = renderedStartDate.clone();
    while (current.isSameOrBefore(renderedEndDate)) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    return dates;
  }, [renderedStartDate, renderedEndDate]);

  // Generate array of currently visible dates
  const visibleDates = useMemo(() => {
    const dates = [];
    const current = visibleRange.start.clone();
    while (current.isSameOrBefore(visibleRange.end)) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    return dates;
  }, [visibleRange]);

  // Calculate data range for GraphQL query (extends beyond rendered range)
  const dataRange = useMemo(() => {
    return {
      start: renderedStartDate.clone().subtract(30, 'days'),
      end: renderedEndDate.clone().add(30, 'days'),
    };
  }, [renderedStartDate, renderedEndDate]);

  // Load data for the extended range
  const { loading, error, data } = useQuery(GET_TIMELINE_ORDERS, {
    variables: {
      startDate: dataRange.start.format('YYYY-MM-DD'),
      endDate: dataRange.end.format('YYYY-MM-DD'),
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  });

  // Load booth count for availability calculation
  const { data: boothData } = useQuery(GET_BOOTH_COUNT, {
    fetchPolicy: 'cache-first',
  });

  // Calculate projected booth assignments for orders without booth_id
  const calculateProjectedBoothAssignments = useCallback(orders => {
    // Get all available booth IDs (assuming they're numeric and we want to start from the smallest)
    const allBoothIds = [...new Set(orders.filter(o => o.booth_id).map(o => o.booth_id))].sort((a, b) => a - b);

    // Track projected assignments to avoid conflicts
    const projectedAssignments = new Map(); // boothId -> [{ shipDate, returnDate }]

    // Orders without booth_id, sorted by ship_date
    const unassignedOrders = orders
      .filter(order => !order.booth_id || order.booth_id === 0)
      .sort((a, b) => moment(a.ship_date).unix() - moment(b.ship_date).unix());

    const ordersWithProjections = [...orders];

    unassignedOrders.forEach(order => {
      if (!order.ship_date || !order.return_date) return;

      const shipDate = moment(order.ship_date);
      const returnDate = moment(order.return_date);

      // Find first available booth
      for (const boothId of allBoothIds) {
        let isAvailable = true;

        // Check against actual orders for this booth
        const actualOrdersForBooth = orders.filter(o => o.booth_id === boothId);
        for (const existingOrder of actualOrdersForBooth) {
          if (!existingOrder.return_date || !existingOrder.ship_date) continue;

          const existingReturn = moment(existingOrder.return_date);
          const existingShip = moment(existingOrder.ship_date);

          // Booth is not available if there's overlap (with 1-day processing buffer)
          if (shipDate.isSameOrBefore(existingReturn.clone().add(1, 'day')) && returnDate.isSameOrAfter(existingShip)) {
            isAvailable = false;
            break;
          }
        }

        // Check against projected orders for this booth
        if (isAvailable && projectedAssignments.has(boothId)) {
          const projectedOrders = projectedAssignments.get(boothId);
          for (const projected of projectedOrders) {
            // Booth is not available if there's overlap (with 1-day processing buffer)
            if (
              shipDate.isSameOrBefore(projected.returnDate.clone().add(1, 'day')) &&
              returnDate.isSameOrAfter(projected.shipDate)
            ) {
              isAvailable = false;
              break;
            }
          }
        }

        if (isAvailable) {
          // Assign this booth as projection
          const orderIndex = ordersWithProjections.findIndex(o => o.id === order.id);
          if (orderIndex >= 0) {
            ordersWithProjections[orderIndex] = {
              ...order,
              booth_id: boothId,
              _isProjectedBooth: true, // Flag to indicate this is a projection
            };
          }

          // Track this projection
          if (!projectedAssignments.has(boothId)) {
            projectedAssignments.set(boothId, []);
          }
          projectedAssignments.get(boothId).push({ shipDate, returnDate });

          break; // Found a booth, move to next order
        }
      }
    });

    return ordersWithProjections;
  }, []);

  // Update orders when data changes - preserve scroll position
  useEffect(() => {
    if (data && data.orders && Array.isArray(data.orders)) {
      // Store current scroll position before updating orders
      const currentScrollLeft = scrollRef.current?.scrollLeft;

      // Apply projected booth assignments to orders without booth_id
      const ordersWithProjections = calculateProjectedBoothAssignments(data.orders);
      setOrders(ordersWithProjections);

      // Restore scroll position after data update
      if (currentScrollLeft !== undefined && scrollRef.current && initialScrollDone.current) {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollLeft = currentScrollLeft;
          }
        });
      }
    }
  }, [data, calculateProjectedBoothAssignments]);

  // Filter orders that should be visible in current date range
  const visibleOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];

    return orders.filter(order => {
      const shipDate = order.ship_date ? moment(order.ship_date) : null;
      const returnDate = order.return_date ? moment(order.return_date) : null;

      if (!shipDate || !returnDate) return false;

      // Order is visible if it overlaps with rendered range (not just visible range)
      return shipDate.isSameOrBefore(renderedEndDate) && returnDate.isSameOrAfter(renderedStartDate);
    });
  }, [orders, renderedStartDate, renderedEndDate]);

  // Calculate daily statistics for ALL rendered dates (not just visible ones)
  const dailyStats = useMemo(() => {
    const stats = {};

    // Initialize stats for all rendered dates
    renderedDates.forEach(date => {
      const dateKey = date.format('YYYY-MM-DD');
      stats[dateKey] = {
        events: new Set(),
        shipping: new Set(),
        returning: new Set(),
        inUse: new Set(),
      };
    });

    orders.forEach(order => {
      if (!order.ship_date || !order.return_date) return;

      const shipDate = moment(order.ship_date);
      const eventDate = moment(order.event_date);
      const returnDate = moment(order.return_date);
      const returnShipDate = moment(order.return_ship_date);

      renderedDates.forEach(date => {
        const dateKey = date.format('YYYY-MM-DD');

        if (date.isSame(eventDate, 'day')) {
          stats[dateKey].events.add(order.id);
        }

        if (date.isSame(shipDate, 'day')) {
          stats[dateKey].shipping.add(order.id);
        }

        if (date.isSame(returnShipDate, 'day') || date.isSame(returnDate, 'day')) {
          stats[dateKey].returning.add(order.id);
        }

        if (date.isSameOrAfter(shipDate, 'day') && date.isSameOrBefore(returnDate, 'day')) {
          stats[dateKey].inUse.add(order.id);
        }
      });
    });

    // Convert Sets to counts
    Object.keys(stats).forEach(dateKey => {
      stats[dateKey] = {
        events: stats[dateKey].events.size,
        shipping: stats[dateKey].shipping.size,
        returning: stats[dateKey].returning.size,
        inUse: stats[dateKey].inUse.size,
      };
    });

    return stats;
  }, [renderedDates, orders]);

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    setDaysVisible(prev => Math.max(7, prev - 7));
  }, []);

  const handleZoomOut = useCallback(() => {
    setDaysVisible(prev => Math.min(28, prev + 7));
  }, []);

  // Handle going to today
  const handleGoToToday = useCallback(() => {
    if (scrollRef.current) {
      const today = moment();
      const todayIndex = renderedDates.findIndex(date => date.isSame(today, 'day'));

      if (todayIndex >= 0) {
        const scrollLeft = Math.max(0, todayIndex * cellWidth - scrollRef.current.clientWidth / 2);
        scrollRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      } else {
        // If today is not in rendered range, reset the range around today
        setRenderedStartDate(today.clone().subtract(60, 'days'));
        setRenderedEndDate(today.clone().add(60, 'days'));
        // Mark initial scroll as not done so it will scroll to today when dates update
        initialScrollDone.current = false;
      }
    }
  }, [renderedDates, cellWidth]);

  // Handle date header click for sorting
  const handleDateHeaderClick = useCallback(date => {
    const dateKey = date.format('YYYY-MM-DD');
    // Toggle sort: if clicking the same date, clear the sort; otherwise set new sort date
    setSelectedSortDate(prev => (prev === dateKey ? null : dateKey));
  }, []);

  // Calculate rental bar position and width for continuous rendering with segments
  const calculateRentalBar = useCallback(
    order => {
      const shipDate = order.ship_date ? moment(order.ship_date) : null;
      const arrivalDate = order.arrival_date ? moment(order.arrival_date) : null;
      const eventDate = order.event_date ? moment(order.event_date) : null;
      const returnShipDate = order.return_ship_date ? moment(order.return_ship_date) : null;
      const returnDate = order.return_date ? moment(order.return_date) : null;

      if (!shipDate || !returnDate) return null;

      // Find the start and end positions relative to rendered dates
      const startIndex = renderedDates.findIndex(date => date.isSameOrAfter(shipDate, 'day'));
      const endIndex = renderedDates.findIndex(date => date.isAfter(returnDate, 'day'));

      const actualStartIndex = Math.max(0, startIndex);
      const actualEndIndex = endIndex === -1 ? renderedDates.length : endIndex;

      if (actualStartIndex >= actualEndIndex) return null;

      const left = actualStartIndex * cellWidth;
      const width = (actualEndIndex - actualStartIndex) * cellWidth;

      // Calculate segments within the bar for different phases
      const segments = [];

      for (let i = actualStartIndex; i < actualEndIndex; i++) {
        const date = renderedDates[i];
        let phase = 'shipping';

        if (arrivalDate && date.isSameOrAfter(arrivalDate, 'day') && eventDate && date.isBefore(eventDate, 'day')) {
          phase = 'delivered';
        } else if (eventDate && date.isSame(eventDate, 'day')) {
          phase = 'event';
        } else if (
          eventDate &&
          date.isAfter(eventDate, 'day') &&
          returnShipDate &&
          date.isBefore(returnShipDate, 'day')
        ) {
          phase = 'packing';
        } else if (returnShipDate && date.isSameOrAfter(returnShipDate, 'day')) {
          phase = 'returning';
        }

        segments.push({
          phase,
          left: i * cellWidth,
          width: cellWidth,
          isFirst: i === actualStartIndex,
          isLast: i === actualEndIndex - 1,
        });
      }

      return {
        left,
        width,
        order,
        segments,
      };
    },
    [renderedDates, cellWidth]
  );

  // Get visible rental bars
  const rentalBars = useMemo(() => {
    return visibleOrders.map(calculateRentalBar).filter(Boolean);
  }, [visibleOrders, calculateRentalBar]);

  // Group rental bars by booth_id and assign consistent row indices
  const groupedRentalBars = useMemo(() => {
    const boothGroups = {};
    const updatedMapping = { ...boothRowMapping };
    let currentNextRowIndex = nextRowIndex;

    // Group bars by booth_id and assign stable row indices
    rentalBars.forEach(bar => {
      const boothId = bar.order.booth_id;

      // Assign row index if this booth doesn't have one yet
      if (updatedMapping[boothId] === undefined) {
        updatedMapping[boothId] = currentNextRowIndex++;
      }

      if (!boothGroups[boothId]) {
        boothGroups[boothId] = [];
      }
      boothGroups[boothId].push(bar);
    });

    // Update state if we added new booths
    if (currentNextRowIndex > nextRowIndex) {
      setBoothRowMapping(updatedMapping);
      setNextRowIndex(currentNextRowIndex);
    }

    // Convert to array format for rendering
    let grouped = Object.keys(boothGroups).map(boothId => ({
      boothId,
      rowIndex: updatedMapping[boothId],
      bars: boothGroups[boothId],
    }));

    // Apply sorting if a date is selected
    if (selectedSortDate) {
      const sortDate = moment(selectedSortDate);

      // Separate orders into two groups: active on selected date and others
      const activeOnDate = [];
      const notActiveOnDate = [];

      grouped.forEach(group => {
        const hasActiveOrderOnDate = group.bars.some(bar => {
          const order = bar.order;
          const shipDate = moment(order.ship_date);
          const returnDate = moment(order.return_date);
          return sortDate.isSameOrAfter(shipDate, 'day') && sortDate.isSameOrBefore(returnDate, 'day');
        });

        if (hasActiveOrderOnDate) {
          activeOnDate.push(group);
        } else {
          notActiveOnDate.push(group);
        }
      });

      // Sort active orders by booth_id numerically
      activeOnDate.sort((a, b) => parseInt(a.boothId) - parseInt(b.boothId));

      // Sort non-active orders by booth_id as well for consistency
      notActiveOnDate.sort((a, b) => parseInt(a.boothId) - parseInt(b.boothId));

      // Combine: active orders first, then others
      grouped = [...activeOnDate, ...notActiveOnDate];
    } else {
      // Default sort by row index (original booth appearance order)
      grouped.sort((a, b) => a.rowIndex - b.rowIndex);
    }

    return grouped;
  }, [rentalBars, boothRowMapping, nextRowIndex, selectedSortDate]);

  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (scrollRef.current) {
        setContainerWidth(scrollRef.current.clientWidth);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
      handleResize(); // Initial call
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Infinite scroll implementation - extend buffer when approaching edges
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollLeft: currentScrollLeft, scrollTop: currentScrollTop, scrollWidth, clientWidth } = scrollElement;

      // Update scroll position state for date range updates
      setScrollLeft(currentScrollLeft);
      setScrollTop(currentScrollTop);

      // Throttle only the buffer extension logic
      throttledBufferExtension(currentScrollLeft, scrollWidth, clientWidth);
    };

    const handleBufferExtension = (currentScrollLeft, scrollWidth, clientWidth) => {
      const totalRenderedWidth = renderedDates.length * cellWidth;

      // Calculate position relative to rendered content
      const scrollPercentage = currentScrollLeft / (totalRenderedWidth - clientWidth);

      // Extend buffer when approaching edges (10% threshold)
      if (scrollPercentage < 0.1) {
        // Approaching left edge - extend earlier dates
        const currentScrollDate = renderedStartDate.clone().add(Math.floor(currentScrollLeft / cellWidth), 'days');
        setRenderedStartDate(prev => {
          const newStart = prev.clone().subtract(30, 'days');
          // Adjust scroll position to maintain current view
          requestAnimationFrame(() => {
            if (scrollRef.current) {
              const adjustment = 30 * cellWidth;
              scrollRef.current.scrollLeft = currentScrollLeft + adjustment;
              setScrollLeft(currentScrollLeft + adjustment); // Update state as well
            }
          });
          return newStart;
        });
      } else if (scrollPercentage > 0.9) {
        // Approaching right edge - extend later dates
        setRenderedEndDate(prev => prev.clone().add(30, 'days'));
      }
    };

    // Throttle only the buffer extension logic
    let timeoutId;
    const throttledBufferExtension = (currentScrollLeft, scrollWidth, clientWidth) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => handleBufferExtension(currentScrollLeft, scrollWidth, clientWidth), 100);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [renderedDates.length, cellWidth]);

  // Initial scroll to today - only run once on mount
  useEffect(() => {
    if (scrollRef.current && renderedDates.length > 0 && !initialScrollDone.current) {
      const todayIndex = renderedDates.findIndex(date => date.isSame(moment(), 'day'));
      if (todayIndex >= 0) {
        const scrollElement = scrollRef.current;
        const scrollLeftPosition = Math.max(0, todayIndex * cellWidth - scrollElement.clientWidth / 2);

        // Use timeout to ensure layout is complete
        setTimeout(() => {
          if (scrollElement) {
            scrollElement.scrollLeft = scrollLeftPosition;
            setScrollLeft(scrollLeftPosition); // Update state as well
            initialScrollDone.current = true; // Mark as done
          }
        }, 100);
      }
    }
  }, [renderedDates.length, cellWidth]); // Only depend on essential layout properties

  if (error) {
    return (
      <div className={classes.noRentals}>
        <Typography color='error'>Error loading timeline: {error.message}</Typography>
      </div>
    );
  }

  const totalBooths = boothData?.booths_aggregate?.aggregate?.count || 0;

  return (
    <div className={classes.root}>
      {/* Timeline - now the primary scrollable surface */}
      <div ref={scrollRef} className={classes.timelineContainer}>
        <div className={classes.timeline} style={{ width: renderedDates.length * cellWidth }}>
          {/* Date Header - render headers for all rendered dates for infinite scroll */}
          <div className={classes.dateHeader}>
            {renderedDates.map(date => {
              const isToday = date.isSame(moment(), 'day');
              const isWeekend = [0, 6].includes(date.day());
              const dateKey = date.format('YYYY-MM-DD');
              const isSelected = selectedSortDate === dateKey;

              const stats = dailyStats[dateKey] || {};
              const available = totalBooths - (stats.inUse || 0);

              return (
                <div
                  key={dateKey}
                  className={`${classes.dateCell} ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''} ${
                    isSelected ? 'selected' : ''
                  }`}
                  style={{ width: cellWidth, minWidth: cellWidth, flexShrink: 0 }}
                  onClick={() => handleDateHeaderClick(date)}
                >
                  <div className={classes.dateInfo}>
                    <div className={classes.dateDay}>{date.format('ddd')}</div>
                    <div className={classes.dateNum}>{date.format('D')}</div>
                    <div className={classes.dateMonth}>{date.format('MMM')}</div>
                  </div>

                  <div className={classes.dateStats}>
                    <Tooltip title='Booths shipping today' placement='top'>
                      <div
                        className={classes.statBox}
                        style={{
                          backgroundColor: stats.shipping > 0 ? '#FF9800' : 'transparent',
                          color: stats.shipping > 0 ? 'white' : 'transparent',
                        }}
                      >
                        {stats.shipping || 0}
                      </div>
                    </Tooltip>
                    <Tooltip title='Events happening today' placement='top'>
                      <div
                        className={classes.statBox}
                        style={{
                          backgroundColor: stats.events > 0 ? '#2E7D32' : 'transparent',
                          color: stats.events > 0 ? 'white' : 'transparent',
                        }}
                      >
                        {stats.events || 0}
                      </div>
                    </Tooltip>
                    <Tooltip title='Booths returning today' placement='top'>
                      <div
                        className={classes.statBox}
                        style={{
                          backgroundColor: stats.returning > 0 ? '#607D8B' : 'transparent',
                          color: stats.returning > 0 ? 'white' : 'transparent',
                        }}
                      >
                        {stats.returning || 0}
                      </div>
                    </Tooltip>
                    <Tooltip title='Available booths (not in use)' placement='top'>
                      <div
                        className={classes.statBox}
                        style={{
                          backgroundColor: available > 0 ? '#9E9E9E' : 'transparent',
                          color: available > 0 ? 'white' : 'transparent',
                        }}
                      >
                        {available || 0}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rental Bars Container */}
          <div
            style={{
              position: 'relative',
              minHeight: groupedRentalBars.length * (viewMode === 'expanded' ? 80 : 45),
              zIndex: 1,
            }}
          >
            {/* Weekend background stripes */}
            {renderedDates.map((date, index) => {
              const isWeekend = [0, 6].includes(date.day()); // Saturday = 6, Sunday = 0
              if (!isWeekend) return null;

              return (
                <div
                  key={`weekend-${date.format('YYYY-MM-DD')}`}
                  className={classes.weekendStripe}
                  style={{
                    left: index * cellWidth,
                    width: cellWidth,
                  }}
                />
              );
            })}

            {/* Rental Bars */}
            {loading && orders.length === 0 ? (
              <Loading />
            ) : groupedRentalBars.length === 0 ? (
              <div className={classes.noRentals}>
                <Typography>No active rentals in this time period</Typography>
              </div>
            ) : (
              groupedRentalBars.map(group => (
                <div
                  key={group.boothId}
                  className={classes.timelineRow}
                  style={{ height: viewMode === 'expanded' ? 80 : 45 }}
                >
                  {/* In-row booth header - pinned to left side but doesn't take up layout space */}
                  <div style={{ position: 'relative', height: 0 }}>
                    <div
                      className={classes.inRowBoothHeader}
                      style={{
                        height: viewMode === 'expanded' ? 80 : 45, // Match row height for proper centering
                      }}
                    >
                      <BoothIdLink boothId={group.boothId} size='large' />
                    </div>
                  </div>

                  {/* All orders for this booth */}
                  {group.bars.map(bar => {
                    const order = bar.order;
                    return (
                      <div key={order.id} style={{ position: 'relative' }}>
                        {/* Render segments for visual phases */}
                        {bar.segments.map((segment, segIndex) => (
                          <Tooltip
                            key={`${order.id}-${segIndex}`}
                            title={
                              <div>
                                <div>
                                  <strong>
                                    #{order.id} - {order.event_name}
                                  </strong>
                                </div>
                                <div>{order.shipping_city}</div>
                                <div>Event: {moment(order.event_date).format('MMM D, YYYY')}</div>
                                <div>
                                  Ship: {moment(order.ship_date).format('MMM D')} → Return:{' '}
                                  {moment(order.return_date).format('MMM D')}
                                </div>
                                <div>
                                  Booth: {order.booth_id}
                                  {order._isProjectedBooth && (
                                    <span style={{ color: '#FF9800', fontWeight: 'bold' }}> (Projected)</span>
                                  )}
                                </div>
                                <div>Transit: {order.transit_days} days</div>
                                <div>Phase: {segment.phase}</div>
                                {order._isProjectedBooth && (
                                  <div style={{ color: '#FF9800', fontSize: '0.8em', marginTop: 4 }}>
                                    * This booth assignment is projected based on availability
                                  </div>
                                )}
                              </div>
                            }
                          >
                            <div
                              className={`${classes.rentalBar} ${
                                viewMode === 'expanded' ? classes.rentalBarExpanded : classes.rentalBarDense
                              } ${classes[`phase-${segment.phase}`]} ${segment.isFirst ? classes.rentalBarFirst : ''} ${
                                segment.isLast ? classes.rentalBarLast : ''
                              }`}
                              style={{
                                left: segment.left,
                                width: segment.width,
                                zIndex: 2,
                              }}
                              onClick={() => (window.location.href = `/orders/${order.id}/`)}
                            />
                          </Tooltip>
                        ))}

                        {/* Content overlay spanning the entire order bar */}
                        <div
                          style={{
                            position: 'absolute',
                            left: bar.left,
                            width: bar.width,
                            height: viewMode === 'expanded' ? 70 : 40, // Increased dense height from 35 to 40
                            top: viewMode === 'expanded' ? 5 : 2.5, // Keep same top position
                            zIndex: 3, // Above the segments
                            pointerEvents: 'none', // Allow clicks to pass through to segments
                            display: 'flex',
                            alignItems: viewMode === 'expanded' ? 'stretch' : 'center',
                            padding: viewMode === 'expanded' ? '12px 16px' : '2px 8px', // Reduced dense padding from 10px to 2px vertically
                            boxSizing: 'border-box',
                          }}
                          onClick={() => (window.location.href = `/orders/${order.id}/`)}
                        >
                          {viewMode === 'expanded' ? (
                            // Expanded view: full information across entire bar
                            <div
                              style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                height: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  width: '100%',
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: '0.9rem',
                                      fontWeight: 600,
                                      color: 'white',
                                      lineHeight: 1.2,
                                      marginBottom: 3,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    #{order.id} - {order.event_name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      color: 'white',
                                      opacity: 0.95,
                                      lineHeight: 1.1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {order.shipping_city}
                                  </div>
                                </div>
                                {order._isProjectedBooth && (
                                  <div
                                    style={{
                                      fontSize: '0.65rem',
                                      color: '#FFB74D',
                                      fontWeight: 'bold',
                                      textAlign: 'right',
                                      lineHeight: 1,
                                      marginLeft: 8,
                                      flexShrink: 0,
                                    }}
                                  >
                                    Projected
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: '0.7rem',
                                  color: 'white',
                                  opacity: 0.9,
                                  width: '100%',
                                  fontWeight: 500,
                                  lineHeight: 1.1,
                                }}
                              >
                                <div>Ship: {moment(order.ship_date).format('MMM D')}</div>
                                <div>Event: {moment(order.event_date).format('MMM D')}</div>
                                <div>Return: {moment(order.return_date).format('MMM D')}</div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: '0.65rem',
                                  color: 'white',
                                  opacity: 0.85,
                                  width: '100%',
                                  fontWeight: 400,
                                  lineHeight: 1.1,
                                }}
                              >
                                <div>Props: {order.props_name || 'Default'}</div>
                                <div>Transit: {order.transit_days}d</div>
                                <div>Print: {order.print_quantity || 'N/A'}</div>
                              </div>
                            </div>
                          ) : (
                            // Dense view: compact information with better sizing
                            <div
                              style={{
                                width: '100%',
                                color: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                height: '100%',
                                gap: '1px',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  lineHeight: 1.1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                #{order.id} - {order.event_name}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.7rem',
                                  fontWeight: 400,
                                  opacity: 0.9,
                                  lineHeight: 1.1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {order.shipping_city} • {order.transit_days}d transit
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Floating controls */}
      <div className={classes.floatingControls}>
        <div className={classes.dateRangeDisplay}>
          <Typography variant='body2'>
            {visibleRange.start.format('MMM D')} - {visibleRange.end.format('MMM D, YYYY')}
          </Typography>
        </div>
        <Button
          variant='contained'
          color='primary'
          size='small'
          startIcon={<Icon>today</Icon>}
          onClick={handleGoToToday}
          className={classes.todayButton}
        >
          Today
        </Button>
      </div>

      {/* Floating Zoom Controls */}
      <div className={classes.zoomControls}>
        <Tooltip title='Zoom in (fewer days)'>
          <IconButton size='small' onClick={handleZoomIn} disabled={daysVisible <= 7} className={classes.zoomButton}>
            <Icon>zoom_in</Icon>
          </IconButton>
        </Tooltip>

        <Typography className={classes.zoomLabel}>{daysVisible} days</Typography>

        <Tooltip title='Zoom out (more days)'>
          <IconButton size='small' onClick={handleZoomOut} disabled={daysVisible >= 28} className={classes.zoomButton}>
            <Icon>zoom_out</Icon>
          </IconButton>
        </Tooltip>
      </div>

      {/* View Mode Toggle */}
      <div className={classes.viewModeToggle}>
        <Button
          variant='contained'
          color={viewMode === 'dense' ? 'primary' : 'secondary'}
          size='small'
          onClick={() => setViewMode('dense')}
          className={classes.viewModeButton}
        >
          Dense
        </Button>
        <Button
          variant='contained'
          color={viewMode === 'expanded' ? 'primary' : 'secondary'}
          size='small'
          onClick={() => setViewMode('expanded')}
          className={classes.viewModeButton}
        >
          Expanded
        </Button>
      </div>
    </div>
  );
}
